import { useState } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { trackFreePlanClicked, trackLogin } from '../../analytics/posthog'
import { useLogin } from '../../auth/useLogin'
import { useI18n, useT } from '../../i18n/useT'
import { formatRenewalDate } from '../../lib/creditRenewal'
import { FREE_MONTHLY_CREDITS, balanceIsMonthly } from '../../lib/freeAllowance'
import { freePlanTierState } from '../../lib/freePlanTier'
import { TIER_ACTION, TierCard } from './TierCard'

interface FreePlanCardProps {
  credits: number
  isSubscribed: boolean
  /** The profile hasn't answered yet — until it does, a subscriber and a
   * free account are indistinguishable. */
  loading: boolean
  /** When this account's transcriptions come back; null when there is
   * nothing honest to promise (see lib/creditRenewal.ts) */
  renewsOn: Date | null
  unit: string
}

/**
 * The free tier, on the shelf where people compare tiers.
 *
 * It already existed and already renewed every month, but the only way to
 * find out was to run out — the offer lived exclusively in the dead end.
 * Here it is the first rung of $0 → $2 → $4 → $9.99, which is also what makes
 * the jump to "20 a month for two dollars" legible.
 *
 * It is not a PlanCard because none of the paid machinery applies —
 * savePendingPlan, the "cancel your current plan first" state, the cancel
 * button — but everything a reader sees is the shared TierCard, so the four
 * rungs cannot drift apart.
 */
export function FreePlanCard({ credits, isSubscribed, loading, renewsOn, unit }: FreePlanCardProps) {
  const { isAuthenticated, isLoading: authLoading } = useAuth0()
  const login = useLogin()
  const { locale } = useI18n()
  const t = useT()
  // The Auth0 round trip takes a beat. PlanCard learned this the hard way:
  // 77% of repeat presses in prod landed within 5 s of the first, on a button
  // that looked dead.
  const [redirecting, setRedirecting] = useState(false)

  const state = freePlanTierState({
    authLoading,
    isAuthenticated,
    profileLoading: loading,
    isSubscribed,
  })

  function handleClick() {
    if (redirecting) return
    trackFreePlanClicked()
    trackLogin()
    setRedirecting(true)
    // Registration, not a purchase — so no savePendingPlan here. There is
    // nothing to resume afterwards, and a stored plan would resurface as an
    // unfinished checkout the moment the account is created.
    //
    // …and back to the uploader, not to the prices: the paid cards return to
    // #pricing because the second half of a purchase is waiting there. Here
    // the transaction is already over.
    setTimeout(() => login({ returnTo: '/#transcription', screenHint: 'signup' }), 150)
  }

  // The terms line describes the TIER, not the reader — Basic states its own
  // to everyone who looks at it, subscriber or not — so the free rung does
  // the same. Only a reader who is actually on it gets them personalised
  // into a balance. Leaving it blank for a subscriber gave that one card an
  // empty bottom half in a row of four, which reads as content that failed
  // to load.
  function bill(): string | undefined {
    if (state === 'unknown') return undefined
    if (state !== 'current') return t('pricing.billFree')
    // A balance above the allowance is not monthly and is not at risk — the
    // top-up raises to 3 and never cuts down to it. Promising its return is
    // promising to take most of it away, and it is said to exactly the person
    // with something to lose: someone who cancelled a plan and still holds
    // what they paid for.
    if (!balanceIsMonthly(credits)) return t('pricing.billFreeKept', { count: credits })
    return renewsOn
      ? t('pricing.billFreeLeft', { count: credits, date: formatRenewalDate(renewsOn, locale) })
      : t('pricing.billFreeLeftNoDate', { count: credits })
  }

  return (
    <TierCard
      name={t('pricing.free.name')}
      price="$0"
      count={FREE_MONTHLY_CREDITS}
      unit={unit}
      bill={bill()}
      badge={state === 'current' ? { text: t('pricing.active'), tone: 'current' } : undefined}
      own={state === 'current'}
    >
      {state === 'register' && (
        <button
          onClick={handleClick}
          disabled={redirecting}
          aria-busy={redirecting}
          className={`${TIER_ACTION} border border-primary transition-colors ${
            redirecting ? 'text-primary/70 cursor-wait' : 'text-primary hover:bg-primary/10 cursor-pointer'
          }`}
        >
          {redirecting ? (
            <span className="inline-flex items-center justify-center gap-1.5">
              <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
              </svg>
              {t('pricing.redirecting')}
            </span>
          ) : (
            t('limit.createAccount')
          )}
        </button>
      )}

      {state === 'current' && (
        <div className={`${TIER_ACTION} border border-border text-text-muted`}>
          {t('pricing.free.current')}
        </div>
      )}

      {/* A subscriber has nothing to press here, but the slot still has
          something true to say — and saying it keeps the rung from being the
          one card in the row with a blank lower half.
          Bordered like a disabled control, not because it is one: the four
          cards read as a row — Basic ends in a Cancel button, Pro and
          Ultimate in a disabled Subscribe — and a borderless pill on the
          fourth reads as content that failed to load. Symmetry across the
          row beats the semantic honesty of one card. */}
      {state === 'subscribed' && (
        <div className={`${TIER_ACTION} border border-border text-text-muted`}>
          {t('pricing.free.included')}
        </div>
      )}
    </TierCard>
  )
}
